import { TicketStatus, UserRole, hasAdminAuthority, mayAssignWork, roleSatisfies, userRoleSchema, } from '../enums.js';
/**
 * Who may sign off someone else's work.
 *
 * FRONT_DESK is in because the desk fields the guest and is who confirms the room is
 * actually usable again — the same reasoning that puts it in `mayAssignWork`, and the
 * reason this set is not a prefix of the authority hierarchy.
 *
 * SUPERVISOR is deliberately out. D24 made it an authority tier that holds no admin
 * rights, and verification is an administrative act.
 *
 * SUPER_ADMIN is named explicitly even though `roleSatisfies` would reach it through
 * ADMIN, because the set is read as documentation of who verifies.
 */
export const VERIFIERS = [UserRole.FrontDesk, UserRole.Admin, UserRole.SuperAdmin];
/**
 * Whether this role may verify, close or reject.
 *
 * The one gate both sides call. The server used a literal `includes` on `VERIFIERS`,
 * which skipped role inheritance and left SUPER_ADMIN unable to verify through the
 * normal route at all — root had to fall back to the super-admin override. Meanwhile
 * the client gated on `hasAdminAuthority`, a different set entirely: FRONT_DESK was
 * allowed by the server but shown no button, and SUPER_ADMIN was shown a button the
 * server refused. Same function on both ends is what stops that recurring.
 *
 * Takes a raw string and fails closed on anything unrecognised, as the other authority
 * checks do — callers on the client hold a role that came off the wire.
 */
export function canVerify(role) {
    const parsed = userRoleSchema.safeParse(role);
    return parsed.success && roleSatisfies(parsed.data, VERIFIERS);
}
/**
 * Who may mark work complete.
 *
 * A role set rather than `{ kind: 'assignee' }`. Assignee-only completion encoded the
 * assumption that whoever starts a job is whoever finishes it, which in a hotel is
 * routinely false: a job claimed on the early shift is often finished on the late one.
 * Refusing the finisher does not bring the early shift back — it makes the work go
 * unrecorded, or recorded under someone else's login.
 *
 * Every role that actually does the work is in, plus the three authority tiers: a duty
 * manager or a manager picking a job up themselves is an ordinary night. SUPERVISOR is
 * out of `VERIFIERS` because verification is an administrative act (D24) — doing the work
 * is not, so it is in here.
 *
 * FRONT_DESK and STAFF are in for a structural reason, not a generous one. `canClaim`
 * gates on the actor's stored department (D22), never on their role, so a FRONT_DESK user
 * in FRONT_OFFICE and a STAFF user in FOOD_BEVERAGE or SECURITY can already claim their
 * own department's jobs — exactly the departments-with-no-matching-role case that check
 * calls out. A role that may claim a job but may not finish it is a dead end.
 *
 * AUDITOR is out: D24 makes it read-oriented compliance, and asserting that a fault was
 * fixed is not an observation. EXTERNAL_VENDOR is out as a limitation rather than a
 * judgement — a vendor does do the work, but a role set cannot say "only the job shared
 * with them", and vendor access scope is still open (SPEC.md §9.2). On the role alone,
 * one vendor account could resolve every job in the property.
 *
 * ADMIN and SUPER_ADMIN would each be reached by `roleSatisfies` from the tier below, so
 * only SUPERVISOR is load-bearing among the three. All three are named for the reason
 * `VERIFIERS` names SUPER_ADMIN: the set is read as documentation of who completes.
 */
export const COMPLETERS = [
    UserRole.Housekeeping,
    UserRole.Maintenance,
    UserRole.Technician,
    UserRole.IT,
    UserRole.Staff,
    UserRole.FrontDesk,
    UserRole.Supervisor,
    UserRole.Admin,
    UserRole.SuperAdmin,
];
export const TICKET_TRANSITIONS = [
    {
        from: TicketStatus.Open,
        to: TicketStatus.Escalated,
        labelKey: 'ticket.action.escalate',
        actor: { kind: 'system' },
        requiresReason: false,
    },
    {
        from: TicketStatus.Open,
        to: TicketStatus.InProgress,
        labelKey: 'ticket.action.acknowledge',
        actor: { kind: 'assignee' },
        requiresReason: false,
    },
    {
        from: TicketStatus.Escalated,
        to: TicketStatus.InProgress,
        labelKey: 'ticket.action.acknowledge',
        actor: { kind: 'assignee' },
        requiresReason: false,
    },
    {
        from: TicketStatus.InProgress,
        to: TicketStatus.OnHold,
        labelKey: 'ticket.action.hold',
        // Was `{ kind: 'assignee' }`, which left a manager unable to pause a job somebody
        // else was holding — in their own department or any other. A person who may take
        // a job off someone entirely can certainly pause it, so the same predicate governs.
        actor: { kind: 'assignee-or-director' },
        // Why the job stalled is the whole point of the state — a part, a vendor, access.
        // It matters more now that someone other than the technician can pause the job:
        // "the manager said so" is not an answer they can act on.
        requiresReason: true,
    },
    {
        from: TicketStatus.OnHold,
        to: TicketStatus.InProgress,
        labelKey: 'ticket.action.resume',
        actor: { kind: 'assignee-or-director' },
        requiresReason: false,
    },
    {
        from: TicketStatus.InProgress,
        to: TicketStatus.Resolved,
        labelKey: 'ticket.action.complete',
        // Was `{ kind: 'assignee' }`. The field reality is that a job claimed by the early
        // shift is often finished by the late one, and refusing the finisher is how work goes
        // unrecorded. Any completer role may now finish a job, claimed by them or not, and
        // finishing does not reassign it: `claimedById` stays with the original claimant and
        // `completedById` records who actually finished. The two questions — who took it on,
        // who saw it through — are different, and one field cannot answer both.
        actor: { kind: 'roles', roles: COMPLETERS },
        requiresReason: false,
    },
    {
        from: TicketStatus.Resolved,
        to: TicketStatus.Verified,
        labelKey: 'ticket.action.verify',
        actor: { kind: 'roles', roles: VERIFIERS },
        requiresReason: false,
    },
    {
        from: TicketStatus.Resolved,
        to: TicketStatus.InProgress,
        labelKey: 'ticket.action.reject',
        actor: { kind: 'roles', roles: VERIFIERS },
        // The technician needs to know what was wrong with the fix.
        requiresReason: true,
    },
    {
        // Closure is a second act, not the same one. RESOLVED → CLOSED used to be a single
        // transition labelled "verify and close", which could not distinguish a job someone
        // actually inspected from one tidied off the board. There is deliberately no edge
        // from RESOLVED to CLOSED any more: closing passes through VERIFIED or not at all.
        from: TicketStatus.Verified,
        to: TicketStatus.Closed,
        labelKey: 'ticket.action.close',
        actor: { kind: 'roles', roles: VERIFIERS },
        requiresReason: false,
    },
];
/**
 * Whether this user may pick up an UNCLAIMED job (SPEC.md §9.2, decision D19).
 *
 * Only an unassigned ticket can be claimed, and only by someone who works in the
 * department that owns it — or an admin, who covers every department. Once a ticket
 * has an assignee, that person keeps it until a manager reassigns.
 *
 * Compares the actor's own stored department (D22) rather than inferring one from
 * their role: a department with no matching role — FOOD_BEVERAGE, SECURITY — would
 * otherwise be unclaimable by the very people who staff it. `hasAdminAuthority`, not
 * `role === ADMIN`: an exact match refused SUPER_ADMIN, who inherits everything an
 * ADMIN may do (D23) and sits in ADMINISTRATION, so the department arm did not save
 * it either.
 *
 * Lived on the server alone until the toolbar was found offering Claim on every open
 * job to everyone — it switched on status and never asked who held the ticket. The
 * rule moved here so the button and the guard are the same function: the server still
 * enforces it, the client may only ever show what it would allow.
 */
export function canClaim(ticket, actor) {
    if (ticket.assigneeId !== null)
        return false;
    if (hasAdminAuthority(actor.role))
        return true;
    return ticket.department !== null && actor.department === ticket.department;
}
/**
 * Whether this user may move an OPEN or ESCALATED job to IN_PROGRESS: either the job
 * is already theirs, or it is unclaimed and they may claim it. From the technician's
 * side these are one act — the work is theirs and they are beginning it — which is
 * why the server treats claiming as being the assignee (`TransitionService.apply`).
 */
export function canStartWork(ticket, actor) {
    return ticket.assigneeId === actor.id || canClaim(ticket, actor);
}
export function startWorkKind(ticket, actor) {
    if (ticket.assigneeId === actor.id)
        return 'start';
    return canClaim(ticket, actor) ? 'claim' : null;
}
/**
 * The one statement of who may pause or resume a job: its assignee, or anyone
 * `mayAssignWork` admits. `isActorPermitted` evaluates the `assignee-or-director` arm
 * through this, and `canHold` below wraps it for callers holding a ticket, so the
 * table and the button cannot answer differently.
 */
export function holdActorPermitted(isAssignee, role) {
    return isAssignee || mayAssignWork(role);
}
/**
 * Whether this user may put this job on hold, or resume it — the predicate the toolbar
 * gates Hold and Resume on, and the same rule the server enforces through
 * `checkTransition`. Status is not consulted here: that is the transition table's
 * question, and the two are asked together by both sides.
 */
export function canHold(ticket, actor) {
    return holdActorPermitted(ticket.assigneeId === actor.id, actor.role);
}
export function transitionsFrom(status) {
    return TICKET_TRANSITIONS.filter((transition) => transition.from === status);
}
export function findTransition(from, to) {
    return TICKET_TRANSITIONS.find((transition) => transition.from === from && transition.to === to);
}
/**
 * Authoritative guard. The server calls this before writing; the client calls it to
 * decide which buttons to show, so both agree without duplicating the rules.
 */
export function checkTransition(request) {
    const transition = findTransition(request.from, request.to);
    if (!transition)
        return { allowed: false, reason: 'no-such-transition' };
    if (!isActorPermitted(transition.actor, request)) {
        return { allowed: false, reason: 'wrong-actor' };
    }
    if (transition.requiresReason && !request.reason?.trim()) {
        return { allowed: false, reason: 'reason-required' };
    }
    return { allowed: true, transition };
}
function isActorPermitted(actor, request) {
    switch (actor.kind) {
        // Only the escalation timer may escalate; no user can fake it.
        case 'system':
            return false;
        case 'assignee':
            return request.isAssignee;
        // `roleSatisfies`, never a literal `includes`: inheritance applies here exactly as
        // it does everywhere else. A literal membership test is what excluded SUPER_ADMIN
        // from verifying its own property's work.
        case 'roles':
            return roleSatisfies(request.role, actor.roles);
        case 'assignee-or-director':
            return holdActorPermitted(request.isAssignee, request.role);
    }
}
//# sourceMappingURL=ticket-lifecycle.js.map